// Applies a single diff change's reversal directly to a file's content,
// using the same dotted-path vocabulary as the diff response (PRD 23) -
// dict keys and, for a purely-numeric segment, a list index. There is no
// backend endpoint for a single-field revert (PRD 21's DELETE is whole-file
// only), so this mutates a client-held copy of the draft content and PUTs
// the whole file back, exactly as any other edit screen in this app does.
function parseSegments(path: string): (string | number)[] {
  return path.split('.').map((segment) => (/^\d+$/.test(segment) ? Number(segment) : segment))
}

function cloneDeep<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function navigateToParent(root: unknown, segments: (string | number)[]): unknown {
  let node = root
  for (let i = 0; i < segments.length - 1; i++) {
    node = (node as Record<string | number, unknown>)[segments[i]]
  }
  return node
}

// Sets the value at `path` within `content`, returning a new object -
// `content` itself is never mutated. Used when the reverted leaf existed on
// the live side (a plain value change, or a key the draft removed).
export function setValueAtPath<T>(content: T, path: string, value: unknown): T {
  const clone = cloneDeep(content)
  const segments = parseSegments(path)
  const parent = navigateToParent(clone, segments) as Record<string | number, unknown>
  parent[segments[segments.length - 1]] = value
  return clone
}

// Removes the value at `path` within `content` entirely, returning a new
// object. Used when the reverted leaf didn't exist on the live side (the
// draft added it) - for a list index, this splices the element out rather
// than leaving a hole, since JSON has no concept of a sparse array.
//
// Known limitation: if several sibling leaves under the same new object/
// array-entry are each discarded one at a time (e.g. a brand-new lookup
// entry's name/type/aliases/domain, each its own diff row), this can leave
// a partially-formed object behind after only some of them are reverted -
// discarding the file's whole group is the correct action for undoing a
// wholesale addition; per-field discard is intentionally literal (discards
// exactly the one field asked for), not addition-aware.
export function deleteValueAtPath<T>(content: T, path: string): T {
  const clone = cloneDeep(content)
  const segments = parseSegments(path)
  const parent = navigateToParent(clone, segments)
  const lastSegment = segments[segments.length - 1]
  if (Array.isArray(parent) && typeof lastSegment === 'number') {
    parent.splice(lastSegment, 1)
  } else {
    delete (parent as Record<string | number, unknown>)[lastSegment]
  }
  return clone
}
